import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { useAppStateContext } from 'src/AppState/AppState';
import { FooterLegalTextState } from 'src/main/cms/FooterManagement/FooterManagement';
import { FooterLegalTextDataState } from 'src/main/cms/FooterManagement/FooterLegalText/FooterLegalTextDataState';
import { Textarea } from 'src/domains/layouts/shared/formComponents/textarea/Textarea';
import {
    FormComponentWrapper,
    FormWrapper,
    FooterPreview,
    FooterLink,
    FooterNoLink,
    ButtonsInputsWrapper,
    LicenceNumberInputWrapper,
    LicenceNumberLinkInputWrapper,
    InputsWrapper,
} from 'src/main/cms/FooterManagement/FooterLegalText/FooterLegalText.style';
import { FormHeader } from 'src/domains/layouts/shared/cms/form/formHeader/FormHeader';
import { Button } from 'src/domains/layouts/shared/buttons/Button';
import { InputRow } from 'src/domains/layouts/shared/formComponents/input/Input.style';
import { FormButtonsWrapper } from 'src/domains/layouts/shared/buttons/Button.style';
import { Label } from 'src/domains/layouts/shared/typography/Content.style';
import { InfoHint } from 'src/domains/layouts/shared/formComponents/FormComponents.style';

interface FooterLegalTextFormPropsType {
    footerLegalTextState: FooterLegalTextState;
}

export const FooterLegalTextForm = observer(
    'FooterLegalTextForm',
    ({ footerLegalTextState }: FooterLegalTextFormPropsType) => {
        const { configState, trpcClient } = useAppStateContext();
        const [footerState] = useState(
            () => new FooterLegalTextDataState(trpcClient, configState, footerLegalTextState)
        );

        const {
            descriptionText,
            licenceNumber,
            licenceUrl,
            insertLicenceNumber,
            removeLicenceNumberLink,
            disableLicenceNumberInsert,
            resetToDefaultFooterText,
            isFooterCreated,
            handleCreateAndEdit,
            hasInputChanged,
        } = footerState;

        const defaultFooterText = configState.config.footerContentDefault.description_text;
        const defaultLicenceNumber = configState.config.footerContentDefault.licence_number;
        const defaultLicenceUrl = configState.config.footerContentDefault.licence_url;
        const cookiesUrl = configState.config.footerContentDefault.cookies_url;
        const isDefault =
            descriptionText.value === defaultFooterText &&
            licenceNumber.value === defaultLicenceNumber &&
            licenceUrl.value === defaultLicenceUrl;

        const renderPreview = (): React.ReactNode => {
            if (typeof DOMParser === 'undefined') {
                return null;
            }

            const doc = new DOMParser().parseFromString(descriptionText.value, 'text/html');

            const renderLink = (
                url: string | null,
                content: string | React.ReactNode[],
                dataTest?: string
            ): React.JSX.Element =>
                url !== null && url !== '' ? (
                    <FooterLink
                        data-test={dataTest}
                        href={url}
                    >
                        {content}
                    </FooterLink>
                ) : (
                    <FooterNoLink data-test={dataTest}>{content}</FooterNoLink>
                );

            const renderNode = (node: ChildNode, index: number): React.ReactNode => {
                if (node.nodeType === Node.TEXT_NODE) {
                    const text = node.textContent ?? '';

                    if (!text.includes('{{licenceNumber}}')) {
                        return text;
                    }

                    const parts = text.split('{{licenceNumber}}');

                    return parts.map((part, partIndex) => (
                        <React.Fragment key={`${index}-${partIndex}`}>
                            {part}

                            {partIndex < parts.length - 1 &&
                                renderLink(licenceUrl.value, licenceNumber.value, 'licence-number')}
                        </React.Fragment>
                    ));
                }

                if (node.nodeType !== Node.ELEMENT_NODE || !(node instanceof Element)) {
                    return null;
                }

                const children = Array.from(node.childNodes).map(renderNode);

                if (node.tagName.toLowerCase() === 'a') {
                    return renderLink(node.getAttribute('href'), children);
                }

                return React.createElement(node.tagName.toLowerCase(), { key: index }, children);
            };

            return Array.from(doc.body.childNodes).map(renderNode);
        };

        return (
            <FormWrapper>
                <FormHeader
                    isEditMode={false}
                    defaultLabel='Edit footer text'
                />
                <FormComponentWrapper
                    onSubmit={(e: React.FormEvent<HTMLFormElement>): void => {
                        e.preventDefault();
                    }}
                    data-test='footer-legal-text-wrapper'
                >
                    <Label>Preview</Label>
                    <FooterPreview data-test='footer-preview'>
                        {renderPreview()}
                        &nbsp;This site uses cookies, for more information please see our&nbsp;
                        <FooterLink href={cookiesUrl}>cookie policy</FooterLink>.
                    </FooterPreview>
                    {disableLicenceNumberInsert === true ? null : (
                        <InfoHint>Click Insert to embed licence number into footer edit field.</InfoHint>
                    )}
                    <InputRow>
                        <InputsWrapper>
                            <LicenceNumberInputWrapper
                                label='Licence number'
                                formInputState={licenceNumber}
                                dataTest='licence-number-input'
                            />
                            <LicenceNumberLinkInputWrapper
                                label='Licence number link'
                                formInputState={licenceUrl}
                                dataTest='licence-number-link-input'
                            />
                        </InputsWrapper>
                        <ButtonsInputsWrapper>
                            <Button
                                version='secondary'
                                isDisabled={licenceUrl.value === ''}
                                onClick={(): void => removeLicenceNumberLink()}
                                dataTest='remove-licence-number-link-button'
                            >
                                Remove link
                            </Button>
                            <Button
                                isDisabled={disableLicenceNumberInsert === true}
                                onClick={(): void => insertLicenceNumber()}
                                dataTest='insert-licence-number-button'
                            >
                                Insert
                            </Button>
                        </ButtonsInputsWrapper>
                    </InputRow>
                    <Textarea
                        ckeditor
                        config={{
                            toolbar: [['Bold', 'Italic', 'Underline', 'Strike'], ['Link', 'Unlink'], ['Source']],
                            removePlugins: 'resize',
                            enterMode: 2,
                        }}
                        formState={descriptionText}
                        label='Description'
                        dataTest='footer-description'
                    />
                </FormComponentWrapper>

                <FormButtonsWrapper>
                    {isFooterCreated === false ? (
                        <>
                            <Button
                                version='secondary'
                                isDisabled={hasInputChanged === false}
                                onClick={resetToDefaultFooterText}
                                dataTest='reset-footer-button'
                            >
                                Reset footer
                            </Button>
                            <Button onClick={handleCreateAndEdit}>Create footer</Button>
                        </>
                    ) : (
                        <>
                            <Button
                                version='secondary'
                                isDisabled={isDefault}
                                onClick={resetToDefaultFooterText}
                                dataTest='reset-footer-button'
                            >
                                Reset footer
                            </Button>
                            {hasInputChanged === false ? null : (
                                <Button
                                    onClick={handleCreateAndEdit}
                                    dataTest='update-footer-button'
                                >
                                    Update footer
                                </Button>
                            )}
                        </>
                    )}
                </FormButtonsWrapper>
            </FormWrapper>
        );
    }
);
